import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import SiteHeader from './components/sections/SiteHeader';
import SiteFooter from './components/sections/SiteFooter';
import HeroSection from './components/sections/HeroSection';
import StorySection from './components/sections/StorySection';
import ApartmentsSection from './components/sections/ApartmentsSection';
import GallerySection from './components/sections/GallerySection';
import InquirySection from './components/sections/InquirySection';
import LocationSection from './components/sections/LocationSection';
import CookieConsentBanner from './components/common/CookieConsentBanner';
import { useScrollReveal } from './hooks/useScrollReveal';
import { trackPageView, trackSectionView } from './services/analyticsService';

export default function App() {
  const { t, i18n } = useTranslation();
  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://www.villaspilia.gr';
  const canonicalUrl = `${siteUrl.replace(/\/$/, '')}/`;

  useScrollReveal();

  useEffect(() => {
    trackPageView(`${globalThis.location.pathname}${globalThis.location.search}`, document.title);

    const seen = new Set<string>();
    const targets = Array.from(document.querySelectorAll<HTMLElement>('main section[id]'));

    if (targets.length === 0) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const id = (entry.target as HTMLElement).id;
          if (!id || seen.has(id)) return;
          seen.add(id);
          trackSectionView(id);
        });
      },
      { threshold: 0.45, rootMargin: '0px 0px -15% 0px' },
    );

    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{`Villa Spilia — ${t('brand.tagline')}`}</title>
        <meta name="description" content={t('hero.lede')} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="en" href={canonicalUrl} />
        <link rel="alternate" hrefLang="el" href={`${canonicalUrl}?lang=el`} />
        <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
        <meta property="og:url" content={canonicalUrl} />
      </Helmet>

      <a href="#main" className="skip-link">{t('common.skipToContent')}</a>
      <SiteHeader />
      <main id="main">
        <HeroSection />
        <StorySection />
        <ApartmentsSection />
        <GallerySection />
        <InquirySection />
        <LocationSection />
      </main>
      <SiteFooter />
      <CookieConsentBanner />
    </>
  );
}
